<script>
  import { onDestroy, untrack } from 'svelte';
  import { navigate } from '../../lib/stores/router.svelte.js';
  import { authState } from '../../lib/stores/auth.svelte.js';
  import {
    groupsState,
    openGroup,
    closeGroup,
    refreshActiveGroupData,
    getUserRoleInGroup,
  } from '../../lib/stores/groups.svelte.js';
  import {
    updateMemberRole,
    removeMember,
    leaveGroup,
    revokeInviteLink,
  } from '../../lib/services/groups-api.js';
  import {
    canActorTargetMember,
    buildInviteUrl,
    isInviteExpired,
  } from '../../lib/logic/groups-logic.js';
  import InviteModal from './InviteModal.svelte';
  import NotSharedHere from '../shared/NotSharedHere.svelte';

  let { groupId } = $props();

  let showInviteModal = $state(false);
  let actionError = $state('');
  let actionLoading = $state('');

  // My role in this group
  const myRole = $derived(getUserRoleInGroup(groupId));
  const myUid = $derived(authState.user?.uid);
  const isAdminOrOwner = $derived(myRole === 'owner' || myRole === 'admin');

  $effect(() => {
    const id = groupId; // track groupId changes
    untrack(() => openGroup(id));
    return () => untrack(() => closeGroup());
  });

  onDestroy(() => closeGroup());

  function getRoleBadgeClass(role) {
    if (role === 'owner') return 'role-owner';
    if (role === 'admin') return 'role-admin';
    return 'role-member';
  }

  async function handlePromote(member) {
    actionError = '';
    actionLoading = member.uid || member.id;
    try {
      await updateMemberRole(groupId, member.uid || member.id, 'admin');
      await refreshActiveGroupData();
    } catch (err) {
      actionError = err.message || 'Failed to update role.';
    } finally {
      actionLoading = '';
    }
  }

  async function handleDemote(member) {
    actionError = '';
    actionLoading = member.uid || member.id;
    try {
      await updateMemberRole(groupId, member.uid || member.id, 'member');
      await refreshActiveGroupData();
    } catch (err) {
      actionError = err.message || 'Failed to update role.';
    } finally {
      actionLoading = '';
    }
  }

  async function handleRemove(member) {
    const name = member.displayName || member.uid || member.id;
    if (!confirm(`Remove ${name} from the group?`)) return;
    actionError = '';
    actionLoading = member.uid || member.id;
    try {
      await removeMember(groupId, member.uid || member.id);
      await refreshActiveGroupData();
    } catch (err) {
      actionError = err.message || 'Failed to remove member.';
    } finally {
      actionLoading = '';
    }
  }

  async function handleLeave() {
    if (!confirm('Leave this group?')) return;
    actionError = '';
    try {
      await leaveGroup(groupId);
      navigate('groups');
    } catch (err) {
      actionError = err.message || 'Failed to leave group.';
    }
  }

  async function handleRevokeInvite(token) {
    actionError = '';
    actionLoading = 'invite-' + token;
    try {
      await revokeInviteLink(groupId, token);
      await refreshActiveGroupData();
    } catch (err) {
      actionError = err.message || 'Failed to revoke invite.';
    } finally {
      actionLoading = '';
    }
  }

  async function copyInviteUrl(invite) {
    // Use shortCode URL for new invites; fall back to token for legacy invites without shortCode
    const url = invite.shortCode ? buildInviteUrl(invite.shortCode) : buildInviteUrl(invite.token);
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // ignore
    }
  }

  function formatDate(ts) {
    if (!ts) return '';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  }
</script>

<main class="detail-page">
  <div class="detail-container">
    <!-- Back button -->
    <button class="back-btn" onclick={() => navigate('groups')}>
      <svg
        viewBox="0 0 24 24"
        width="16"
        height="16"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
      >
        <polyline points="15 18 9 12 15 6"></polyline>
      </svg>
      Groups
    </button>

    {#if !groupsState.activeGroup}
      <div class="loading">Loading group...</div>
    {:else}
      <div class="group-header">
        <div>
          <h1 class="group-title">{groupsState.activeGroup.name}</h1>
          {#if groupsState.activeGroup.description}
            <p class="group-desc">{groupsState.activeGroup.description}</p>
          {/if}
          <button
            class="group-updates-link"
            onclick={() => navigate('updates', { group: groupId })}
          >
            View update feed
          </button>
        </div>
        {#if myRole !== 'owner'}
          <button class="leave-btn" onclick={handleLeave}>Leave Group</button>
        {/if}
      </div>

      {#if actionError}
        <div class="action-error">{actionError}</div>
      {/if}

      {#if groupsState.activeGroupSubgroups.length > 0}
        <section class="detail-section">
          <div class="section-header">
            <h2 class="section-title">Groups</h2>
          </div>
          <ul class="subgroup-list">
            {#each groupsState.activeGroupSubgroups as sub (sub.groupId)}
              <li class="subgroup-row">
                <span class="subgroup-label">{sub.label}</span>
                <span class="subgroup-count">
                  {sub.memberCount} member{sub.memberCount !== 1 ? 's' : ''}
                </span>
                {#if sub.isMember}<span class="subgroup-you">✓ you</span>{/if}
              </li>
            {/each}
          </ul>
          <p class="subgroup-hint">Join or leave groups from Discord with /groups.</p>
        </section>
      {/if}

      <!-- Members Section -->
      <section class="detail-section">
        <div class="section-header">
          <h2 class="section-title">Members ({groupsState.activeGroupMembers.length})</h2>
        </div>

        <ul class="members-list">
          {#each groupsState.activeGroupMembers as member (member.uid || member.id)}
            {@const memberUid = member.uid || member.id}
            {@const isMe = memberUid === myUid}
            {@const canTarget = canActorTargetMember(myRole, member.role) && !isMe}
            {@const isLoading = actionLoading === memberUid}
            <li class="member-row">
              <div class="member-avatar">
                {#if member.photoURL}
                  <img src={member.photoURL} alt="" referrerpolicy="no-referrer" />
                {:else}
                  <span>{(member.displayName || '?').charAt(0).toUpperCase()}</span>
                {/if}
              </div>
              <div class="member-info">
                <div class="member-name">
                  {member.displayName || memberUid}
                  {#if isMe}<span class="you-badge">You</span>{/if}
                </div>
                {#if member.joinedAt}
                  <div class="member-joined">Joined {formatDate(member.joinedAt)}</div>
                {/if}
                {#if member.subgroupLabels?.length}
                  <div class="member-subgroups">{member.subgroupLabels.join(' · ')}</div>
                {/if}
                {#if member.claimedBinders?.length}
                  <div class="member-binders">
                    {#each member.claimedBinders as binder (binder.url)}
                      <span class="member-claim">
                        <button
                          class="binder-link"
                          class:not-shared={binder.shared === false}
                          onclick={() =>
                            navigate('binder', { binderUrl: binder.url, shortPath: binder.url })}
                        >
                          {binder.name || binder.url}
                        </button>
                        {#if binder.shared === false}<NotSharedHere />{/if}
                      </span>
                    {/each}
                  </div>
                {/if}
              </div>
              <span class="role-badge {getRoleBadgeClass(member.role)}">{member.role}</span>
              {#if canTarget && isAdminOrOwner}
                <div class="member-actions">
                  {#if member.role === 'member' && myRole === 'owner'}
                    <button
                      class="action-btn"
                      disabled={isLoading}
                      onclick={() => handlePromote(member)}
                    >
                      Make Admin
                    </button>
                  {/if}
                  {#if member.role === 'admin' && myRole === 'owner'}
                    <button
                      class="action-btn"
                      disabled={isLoading}
                      onclick={() => handleDemote(member)}
                    >
                      Demote
                    </button>
                  {/if}
                  <button
                    class="action-btn action-btn-danger"
                    disabled={isLoading}
                    onclick={() => handleRemove(member)}
                  >
                    Remove
                  </button>
                </div>
              {/if}
            </li>
          {/each}
        </ul>
      </section>

      <!-- Invite Links Section (admin/owner only) -->
      {#if isAdminOrOwner}
        <section class="detail-section">
          <div class="section-header">
            <h2 class="section-title">Invite Links</h2>
            <button class="create-invite-btn" onclick={() => (showInviteModal = true)}>
              + New Invite
            </button>
          </div>

          {#if groupsState.activeGroupInvites.length === 0}
            <p class="section-empty">
              No active invite links. Create one to share with trading partners.
            </p>
          {:else}
            <ul class="invites-list">
              {#each groupsState.activeGroupInvites as invite (invite.token)}
                {@const expired = isInviteExpired(invite.expiresAt)}
                {@const isLoading = actionLoading === 'invite-' + invite.token}
                <li class="invite-row" class:expired>
                  <div class="invite-info">
                    <div class="invite-token">{invite.token.slice(0, 12)}...</div>
                    <div class="invite-meta">
                      {#if invite.expiresAt && !expired}
                        Expires {formatDate(invite.expiresAt)}
                      {:else if expired}
                        <span class="expired-text">Expired</span>
                      {:else}
                        Never expires
                      {/if}
                    </div>
                  </div>
                  <div class="invite-actions">
                    <button class="action-btn" onclick={() => copyInviteUrl(invite)}> Copy </button>
                    <button
                      class="action-btn action-btn-danger"
                      disabled={isLoading}
                      onclick={() => handleRevokeInvite(invite.token)}
                    >
                      Revoke
                    </button>
                  </div>
                </li>
              {/each}
            </ul>
          {/if}
        </section>
      {/if}
    {/if}
  </div>
</main>

{#if showInviteModal}
  <InviteModal
    {groupId}
    onClose={() => (showInviteModal = false)}
    onCreated={() => refreshActiveGroupData()}
  />
{/if}

<style>
  .detail-page {
    min-height: 100vh;
    padding: 16px 16px 80px;
    display: flex;
    justify-content: center;
  }

  .detail-container {
    width: 100%;
    max-width: 640px;
  }

  .back-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 8px 12px;
    margin-top: 60px;
    margin-bottom: 16px;
    background: transparent;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    color: #6b7280;
    cursor: pointer;
    transition:
      background 0.15s,
      color 0.15s;
    font-family: inherit;
  }

  .back-btn:hover {
    background: rgba(0, 0, 0, 0.05);
    color: #374151;
  }

  .loading {
    text-align: center;
    color: #6b7280;
    padding: 48px 0;
  }

  .group-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 24px;
  }

  .group-title {
    font-size: 1.75rem;
    font-weight: 700;
    color: #1f2937;
    margin: 0 0 4px;
  }

  .group-updates-link {
    margin-top: 8px;
    background: none;
    border: none;
    padding: 0;
    color: #4a90d9;
    cursor: pointer;
    font-size: 14px;
    font-family: inherit;
  }
  .group-updates-link:hover {
    text-decoration: underline;
  }

  .group-desc {
    font-size: 14px;
    color: #6b7280;
    margin: 0;
  }

  .leave-btn {
    padding: 8px 16px;
    background: transparent;
    border: 1px solid #fca5a5;
    border-radius: 8px;
    color: #ef4444;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.15s;
    font-family: inherit;
    flex-shrink: 0;
  }

  .leave-btn:hover {
    background: #fef2f2;
  }

  .action-error {
    font-size: 13px;
    color: #ef4444;
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 8px;
    padding: 10px 16px;
    margin-bottom: 16px;
  }

  .detail-section {
    background: white;
    border-radius: 12px;
    padding: 20px;
    margin-bottom: 16px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  }

  .section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
  }

  .section-title {
    font-size: 1rem;
    font-weight: 600;
    color: #1f2937;
    margin: 0;
  }

  .section-empty {
    font-size: 13px;
    color: #9ca3af;
    margin: 0;
    font-style: italic;
  }

  .create-invite-btn {
    padding: 6px 12px;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    border-radius: 6px;
    color: #1d4ed8;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.15s;
    font-family: inherit;
  }

  .create-invite-btn:hover {
    background: #dbeafe;
  }

  .subgroup-list {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .subgroup-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 0;
    border-bottom: 1px solid #f3f4f6;
    font-size: 14px;
  }

  .subgroup-row:last-child {
    border-bottom: none;
  }

  .subgroup-label {
    flex: 1;
    font-weight: 500;
    color: #1f2937;
  }

  .subgroup-count {
    font-size: 12px;
    color: #9ca3af;
    font-family: var(--font-mono);
  }

  .subgroup-you {
    font-size: 12px;
    color: #6b7280;
  }

  .subgroup-hint {
    margin: 8px 0 0;
    font-size: 12px;
    color: #9ca3af;
  }

  .member-subgroups {
    font-size: 12px;
    color: #9ca3af;
    margin-top: 2px;
  }

  /* Members */
  .members-list,
  .invites-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .member-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 0;
    border-bottom: 1px solid #f3f4f6;
  }

  .member-row:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  .member-avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #e5e7eb;
    overflow: hidden;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 600;
    color: #6b7280;
  }

  .member-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .member-info {
    flex: 1;
    min-width: 0;
  }

  .member-name {
    font-size: 14px;
    font-weight: 500;
    color: #1f2937;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .you-badge {
    font-size: 11px;
    font-weight: 600;
    color: #6b7280;
    background: #f3f4f6;
    border-radius: 999px;
    padding: 1px 6px;
  }

  .member-joined {
    font-size: 12px;
    color: #9ca3af;
    margin-top: 1px;
  }

  .member-binders {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 4px;
  }

  .binder-link {
    display: inline-block;
    padding: 2px 8px;
    background: #f0fdf4;
    border: 1px solid #bbf7d0;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 500;
    color: #15803d;
    cursor: pointer;
    transition: background 0.15s;
    font-family: inherit;
    white-space: nowrap;
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .binder-link:hover {
    background: #dcfce7;
  }

  .member-claim {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }

  /* The viewer's own Claim, hidden from everyone else in this server. */
  .binder-link.not-shared {
    background: #f9fafb;
    border-style: dashed;
    border-color: #d1d5db;
    color: #6b7280;
  }

  .binder-link.not-shared:hover {
    background: #f3f4f6;
  }

  .role-badge {
    font-size: 11px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 999px;
    text-transform: capitalize;
    flex-shrink: 0;
  }

  .role-owner {
    background: #fef3c7;
    color: #92400e;
  }

  .role-admin {
    background: #e0e7ff;
    color: #3730a3;
  }

  .role-member {
    background: #f3f4f6;
    color: #4b5563;
  }

  .member-actions {
    display: flex;
    gap: 6px;
    flex-shrink: 0;
  }

  .action-btn {
    padding: 5px 10px;
    background: transparent;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 500;
    color: #374151;
    cursor: pointer;
    transition: background 0.15s;
    font-family: inherit;
    white-space: nowrap;
  }

  .action-btn:hover:not(:disabled) {
    background: #f3f4f6;
  }

  .action-btn-danger {
    border-color: #fca5a5;
    color: #ef4444;
  }

  .action-btn-danger:hover:not(:disabled) {
    background: #fef2f2;
  }

  .action-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* Invite Links */
  .invite-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 0;
    border-bottom: 1px solid #f3f4f6;
  }

  .invite-row:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  .invite-row.expired {
    opacity: 0.5;
  }

  .invite-info {
    flex: 1;
    min-width: 0;
  }

  .invite-token {
    font-size: 13px;
    font-family: monospace;
    color: #374151;
  }

  .invite-meta {
    font-size: 12px;
    color: #9ca3af;
    margin-top: 2px;
  }

  .expired-text {
    color: #ef4444;
  }

  .invite-actions {
    display: flex;
    gap: 6px;
    flex-shrink: 0;
  }
</style>
